import { watch } from 'vue';
import { useRoute, useRouter, type LocationQueryRaw } from 'vue-router';
import { useChatStore } from '../stores/chatStore';
import { useDataViewStore } from '../stores/dataViewStore';
import { useSessionStore } from '../stores/sessionStore';

/**
 * Two-way sync between session-route query params and the data-view store
 * for the references panel:
 *
 *   /matters/:m/sessions/:s?chat=...&type=...&view=...&item=...
 *
 *   chat — Chat.RecordId (opens the details viewer for that chat)
 *   type — selectedReferenceType (which type tab is active)
 *   view — selectedReferenceUnit (which view variant is active)
 *   item — content_hash of an open communication-raw-view item
 *
 * Push vs replace: chat and item changes push (so back button reverses
 * those navigations); type and view changes replace (UI knobs, not nav).
 *
 * Loops are avoided by comparing the desired query against the current
 * route.query and skipping the router call when they're equal.
 */
export function useReferencesViewUrlSync() {
    const route = useRoute();
    const router = useRouter();
    const chatStore = useChatStore();
    const dataViewStore = useDataViewStore();
    const sessionStore = useSessionStore();

    const KNOWN_KEYS = ['chat', 'type', 'view', 'item'] as const;

    // URL → store. Re-runs when chat list arrives so a cold deep link with
    // ?chat=<id> can resolve once chats finish loading.
    watch(
        [() => route.query, () => chatStore.chats, () => route.name],
        () => {
            if (route.name !== 'session') return;
            const q = route.query;
            const chatId = typeof q.chat === 'string' ? q.chat : null;
            const type = typeof q.type === 'string' ? q.type : null;
            const view = typeof q.view === 'string' ? q.view : null;
            const item = typeof q.item === 'string' ? q.item : null;

            const currentChatId = dataViewStore.selectedChatForDetails?.RecordId ?? null;
            if (chatId !== currentChatId) {
                if (chatId) {
                    const chat = chatStore.chats.find((c) => c.RecordId === chatId);
                    if (chat) dataViewStore.openDetailsViewer(chat);
                    // else: chats not loaded yet, will retry when they arrive.
                } else if (currentChatId) {
                    dataViewStore.closeDetailsViewer();
                }
            }

            if (dataViewStore.selectedReferenceType !== type) {
                dataViewStore.selectedReferenceType = type;
            }
            if (dataViewStore.selectedReferenceUnit !== view) {
                dataViewStore.selectedReferenceUnit = view;
            }

            // Item: route is the source of truth. The actual selection lives
            // in dataViewStore.communicationGroups[currentGroupIndex] and is
            // gated visually by dataViewStore.showDetailView. If URL has an
            // item we ask ReferencesPanel to open it via pendingItemHash;
            // if the URL has none and the detail view is open, close it.
            const currentItemHash = dataViewStore.showDetailView
                ? ((dataViewStore.communicationGroups as Array<{ content_hash?: string }>)[
                      dataViewStore.currentGroupIndex
                  ]?.content_hash ?? null)
                : null;
            if (item !== currentItemHash) {
                if (item) {
                    dataViewStore.pendingItemHash = item;
                } else {
                    if (dataViewStore.showDetailView) {
                        dataViewStore.showDetailView = false;
                        dataViewStore.communicationGroups = [];
                        dataViewStore.currentGroupIndex = 0;
                    }
                    dataViewStore.pendingItemHash = null;
                }
            }
        },
        { immediate: true },
    );

    // Store → URL.
    watch(
        [
            () => dataViewStore.selectedChatForDetails?.RecordId ?? null,
            () => dataViewStore.selectedReferenceType,
            () => dataViewStore.selectedReferenceUnit,
            () => dataViewStore.showDetailView,
            () => dataViewStore.communicationGroups,
            () => dataViewStore.currentGroupIndex,
        ],
        ([chatId, type, view, detailOpen, groups, groupIdx]) => {
            if (route.name !== 'session') return;

            // Only reflect references state into the URL once the route has
            // settled on the session the store currently holds. When the user
            // switches dialogues (or leaves for the matter picker),
            // selectSession() updates currentSessionId synchronously and pushes
            // the new route, while the references teardown that follows fires
            // this watcher before the route commits — at which point
            // route.params still names the *previous* session. Pushing here
            // would target that stale session and abort the user's navigation,
            // leaving them on the old dialogue with only the chat deselected.
            if (route.params.sessionId !== sessionStore.currentSessionId) return;

            const desired: Record<string, string> = {};
            if (chatId) desired.chat = chatId;
            if (type) desired.type = type;
            if (view) desired.view = view;
            if (detailOpen) {
                const group = (groups as Array<{ content_hash?: string }>)[groupIdx as number];
                if (group?.content_hash) desired.item = group.content_hash;
            }

            const currentSubset: Record<string, string> = {};
            for (const k of KNOWN_KEYS) {
                const v = route.query[k];
                if (typeof v === 'string') currentSubset[k] = v;
            }

            const same =
                Object.keys(desired).length === Object.keys(currentSubset).length &&
                Object.keys(desired).every((k) => desired[k] === currentSubset[k]);
            if (same) return;

            const chatChanged = (desired.chat ?? null) !== (currentSubset.chat ?? null);
            const itemChanged = (desired.item ?? null) !== (currentSubset.item ?? null);
            const action: 'push' | 'replace' = chatChanged || itemChanged ? 'push' : 'replace';

            const otherQuery: LocationQueryRaw = { ...route.query };
            for (const k of KNOWN_KEYS) delete otherQuery[k];

            const target = {
                name: route.name as string,
                params: route.params,
                query: { ...otherQuery, ...desired } satisfies LocationQueryRaw,
            };
            if (action === 'push') router.push(target);
            else router.replace(target);
        },
        { flush: 'post' },
    );
}
